import React from "react";
import { drupalSettings } from "../../../utils/config";

/**
 * After the React authorise modal closes, open the same modal as
 * "Admin Bank Transfers" in NautReviewApprovalsForm — in one step, without opening
 * the intermediate Review chooser. Uses a synthetic use-ajax anchor identical to that link,
 * then Drupal.ajax.bindAjaxLinks + click (same mechanism core uses for modal AJAX links).
 *
 * @see \Drupal\naut_dashboard\Form\NautReviewApprovalsForm::buildForm
 */
function openAdminBankTransfersModalAfterReactClose() {
  const yachtId = drupalSettings?.user?.yacht_id;
  if (yachtId == null || yachtId === "" || typeof document === "undefined") {
    return;
  }

  const pathBase = drupalSettings?.path?.baseUrl != null
    ? String(drupalSettings.path.baseUrl).replace(/\/?$/, "/")
    : "/";
  const href = `${pathBase}review-pending-admin-bank-transfers/${yachtId}`;
  const dialogOptions = JSON.stringify({
    dialogClass: "CardholderAdditionalFundsRequests",
  });

  window.setTimeout(() => {
    const anchor = document.createElement("a");
    anchor.id = `js-direct-admin-bank-transfers-${Date.now()}`;
    anchor.href = href;
    anchor.className = "primary_btn use-ajax";
    anchor.setAttribute("data-dialog-type", "modal");
    anchor.setAttribute("data-dialog-options", dialogOptions);
    anchor.setAttribute("aria-hidden", "true");
    anchor.tabIndex = -1;
    anchor.style.cssText = "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;";
    anchor.textContent = "\u200b";

    document.body.appendChild(anchor);

    if (typeof Drupal !== "undefined" && Drupal.ajax?.bindAjaxLinks) {
      Drupal.ajax.bindAjaxLinks(document.body);
    }

    anchor.click();

    window.setTimeout(() => {
      anchor.remove();
    }, 3000);
  }, 400);
}

export const AuthorizeConfirm = ({
  confirmation,
  setStep,
  onYes,
  loading,
  orderId,
  onReceiptDrupalOpenChange,
}) => {
  const [isMobile, setIsMobile] = React.useState(() => (
    typeof window !== "undefined" ? window.innerWidth <= 480 : false
  ));
  const [receiptViewActive, setReceiptViewActive] = React.useState(false);
  const [showAuthorizeConfirm, setShowAuthorizeConfirm] = React.useState(false);
  const receiptOpenedHereRef = React.useRef(false);
  const receiptDialogContentIdRef = React.useRef(null);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIsMobile(window.innerWidth <= 480);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const hasConversion = Boolean(confirmation?.has_conversion);
  const feeDisplay = confirmation?.feeCurrencySign != null && confirmation?.bankTransferFee != null
    ? `${confirmation.feeCurrencySign}${confirmation.bankTransferFee}`
    : null;
  const payloadItem = confirmation?.payload?.[0];
  const beneficiary = payloadItem?.beneficiary;
  const address = beneficiary?.address;
  const bankDetails = beneficiary?.bank_details;

  const currency = confirmation?.sendingCurrency || bankDetails?.account_currency;
  const amountDisplay = confirmation?.sendingAmount || (confirmation?.sell_amount != null ? `${currency || ""} ${confirmation.sell_amount}`.trim() : null);
  const fxRateDisplay = confirmation?.fxRate != null ? `${confirmation.fxRate}` : null;
  const transferFeeDisplay = feeDisplay;
  const willPayDisplay = confirmation?.youWillPay || null;
  const recipientName = bankDetails?.account_name || null;
  const bankName = bankDetails?.bank_name || bankDetails?.bank_country_code || null;
  const accountNumber = bankDetails?.account_number || bankDetails?.iban || null;
  const routingNumber = bankDetails?.routing_number || bankDetails?.bic_swift || null;
  const paymentMethods = Array.isArray(payloadItem?.payment_methods) ? payloadItem.payment_methods : [];
  const transferMethod = paymentMethods.length
    ? paymentMethods
      .map((method) => {
        if (method === "LOCAL") return Drupal.t("Default Local");
        if (method === "SWIFT") return Drupal.t("Fallback SWIFT");
        return method;
      })
      .join(", ")
    : null;
  const clearingSystem = bankDetails?.local_clearing_system || null;

  const countryCode = address?.country_code;
  const countryName = countryCode
    ? (typeof Intl !== "undefined" && Intl.DisplayNames
      ? new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode) || countryCode
      : countryCode)
    : null;
  const recipientAddress = [address?.street_address, address?.city, address?.postcode, countryName]
    .filter(Boolean)
    .join(", ") || null;
  const availableBalance = confirmation?.availableBalancePostTransfer || confirmation?.available_balance_post_transfer || null;
  const sectionHeaderStyle = {
    margin: "0 0 10px 0",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: "#7c7f84",
    textTransform: "uppercase",
  };
  const detailRowStyle = {
    margin: 0,
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "120px 1fr",
    rowGap: isMobile ? "2px" : "0",
    columnGap: "10px",
    alignItems: "center",
    fontSize: isMobile ? "13px" : "14px",
    lineHeight: isMobile ? "20px" : "22px",
    color: "#5e6166",
  };
  const labelStyle = { fontWeight: 500 };
  const valueStyle = { fontWeight: 700, color: "#101114" };
  const showSummary = amountDisplay || transferFeeDisplay || fxRateDisplay || (hasConversion && willPayDisplay);
  const metricCardCount = [amountDisplay, transferFeeDisplay, fxRateDisplay].filter(Boolean).length;
  const metricGridColumns = isMobile
    ? "1fr"
    : (metricCardCount > 0 ? `repeat(${metricCardCount}, minmax(0, 1fr))` : "1fr");
  const metricCardStyle = {
    background: "#f7f8fa",
    border: "none",
    borderRadius: "8px",
    padding: isMobile ? "10px" : "10px 12px",
    minHeight: isMobile ? "60px" : "70px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };
  const renderRow = (label, value) => (value ? (
    <p style={detailRowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </p>
  ) : null);

  const pathBase = drupalSettings?.path?.baseUrl != null
    ? String(drupalSettings.path.baseUrl).replace(/\/?$/, "/")
    : "/";
  const hasReceipt = confirmation?.hasReceipt === true;
  const receiptHref = orderId != null && orderId !== "" && hasReceipt
    ? `${pathBase}open-modal-view-receipt/${orderId}`
    : null;

  React.useEffect(() => {
    if (!receiptHref) {
      setReceiptViewActive(false);
      receiptOpenedHereRef.current = false;
      receiptDialogContentIdRef.current = null;
      onReceiptDrupalOpenChange?.(false);
    }
  }, [receiptHref, onReceiptDrupalOpenChange]);

  // Receipt open swaps this step to sr-only markup, which unmounts the use-ajax link.
  // A remounted anchor must be bound again (bindAjaxLinks only runs once per element via once()).
  React.useLayoutEffect(() => {
    if (!receiptHref || receiptViewActive || typeof Drupal === "undefined" || !Drupal.ajax?.bindAjaxLinks) {
      return;
    }
    Drupal.ajax.bindAjaxLinks(document.body);
  }, [receiptHref, receiptViewActive]);

  React.useEffect(() => {
    if (!receiptHref || typeof document === "undefined" || typeof jQuery === "undefined") {
      return undefined;
    }
    const onDialogAfterCreate = (event) => {
      if (!document.querySelector(".authorise-bank-transfer-popup")) {
        return;
      }
      const contentEl = event.target;
      if (!contentEl?.id || !String(contentEl.id).startsWith("naut-dialog-")) {
        return;
      }
      receiptDialogContentIdRef.current = contentEl.id;
      receiptOpenedHereRef.current = true;
      setReceiptViewActive(true);
      onReceiptDrupalOpenChange?.(true);

      // const $dialog = jQuery(contentEl).closest(".ui-dialog");
      // const $bar = $dialog.find(".ui-dialog-titlebar");
      // if ($bar.length && !$bar.find(".js-naut-receipt-back-confirm").length) {
      //   const $btn = jQuery("<a>", {
      //     class: "btn btn-type-2 reverse go-back close",
      //     text: Drupal.t("Go Back"),
      //   })
      //   $btn.on("click", (e) => {
      //     e.preventDefault();
      //     try {
      //       if (typeof Drupal !== "undefined" && Drupal.dialog) {
      //         Drupal.dialog(contentEl).close();
      //       }
      //       else {
      //         jQuery(contentEl).dialog("close");
      //       }
      //     }
      //     catch (err) {
      //       receiptDialogContentIdRef.current = null;
      //       receiptOpenedHereRef.current = false;
      //       setReceiptViewActive(false);
      //       onReceiptDrupalOpenChange?.(false);
      //     }
      //   });
      //   $bar.append($btn);
      // }
    };
    const onDialogAfterClose = (event) => {
      const contentEl = event.target;
      if (!contentEl?.id || !String(contentEl.id).startsWith("naut-dialog-")) {
        return;
      }
      if (!receiptOpenedHereRef.current) {
        return;
      }
      receiptDialogContentIdRef.current = null;
      receiptOpenedHereRef.current = false;
      setReceiptViewActive(false);
      onReceiptDrupalOpenChange?.(false);
    };
    document.addEventListener("dialog:aftercreate", onDialogAfterCreate);
    document.addEventListener("dialog:afterclose", onDialogAfterClose);
    return () => {
      document.removeEventListener("dialog:aftercreate", onDialogAfterCreate);
      document.removeEventListener("dialog:afterclose", onDialogAfterClose);
    };
  }, [receiptHref, onReceiptDrupalOpenChange]);

  return (
    <>
      <div style={{ padding: isMobile ? "10px 12px" : "14px 16px" }}>
        {receiptViewActive ? (
          <p style={{ position: "absolute", width: "1px", height: "1px", padding: 0, margin: "-1px", overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }} aria-live="polite">
            {Drupal.t("Receipt is open in the dialog above.")}
          </p>
        ) : showAuthorizeConfirm ? (
          <>
            <div className="authorise-bank-transfer-header">
              <h4 className="authorise-bank-transfer-title yellow m-0 p-0">{Drupal.t("Authorize")}</h4>
            </div>
            <div className="authorise-bank-transfer-body-content">
              <div className="authorise-bank-transfer-description black text-center p-0">
                <p>
                  {confirmation?.confirmMessage
                    || Drupal.t("Are you sure you want to authorize this bank transfer?")}
                </p>
              </div>
            </div>
            <div className="authorise-bank-transfer-actions step1-button gap-3 mt-4 mb-0">
              <button
                type="button"
                className="secondary_btn"
                onClick={() => setStep("close")}
                disabled={loading}
              >
                {Drupal.t("No")}
              </button>
              <button
                type="button"
                className="primary_btn"
                onClick={onYes}
                disabled={loading || confirmation?.error}
              >
                {loading ? Drupal.t("Loading…") : Drupal.t("Yes")}
              </button>
            </div>
          </>
        ) : (
          <>
        <div style={{ marginBottom: isMobile ? "12px" : "14px" }}>
          <h4 style={{ margin: "4px 0 0", color: "#101114", fontSize: isMobile ? "20px" : "22px", lineHeight: isMobile ? "26px" : "30px", fontWeight: 700 }}>
            {Drupal.t("Confirm Bank Transfer Details")}
          </h4>
        </div>

        <div>
          {showSummary && (
            <div style={{ marginBottom: isMobile ? "14px" : "18px" }}>
              <div style={{ display: "grid", gap: "10px", gridTemplateColumns: metricGridColumns }}>
                {amountDisplay && (
                  <div style={metricCardStyle}>
                    <p style={{ margin: 0, color: "#74787f", fontSize: "12px", lineHeight: "16px" }}>{Drupal.t("Amount")}</p>
                    <p style={{ margin: "2px 0 0", color: "#101114", fontSize: isMobile ? "20px" : "20px", lineHeight: isMobile ? "26px" : "26px", fontWeight: 700 }}>{amountDisplay}</p>
                  </div>
                )}
                {transferFeeDisplay && (
                  <div style={metricCardStyle}>
                    <p style={{ margin: 0, color: "#74787f", fontSize: "12px", lineHeight: "16px" }}>{Drupal.t("Fee")}</p>
                    <p style={{ margin: "2px 0 0", color: "#101114", fontSize: isMobile ? "20px" : "20px", lineHeight: isMobile ? "26px" : "26px", fontWeight: 700 }}>{transferFeeDisplay}</p>
                  </div>
                )}
                {fxRateDisplay && (
                  <div style={metricCardStyle}>
                    <p style={{ margin: 0, color: "#74787f", fontSize: "12px", lineHeight: "16px" }}>{Drupal.t("FX rate")}</p>
                    <p style={{ margin: "2px 0 0", color: "#101114", fontSize: isMobile ? "20px" : "20px", lineHeight: isMobile ? "26px" : "26px", fontWeight: 700 }}>{fxRateDisplay}</p>
                  </div>
                )}
              </div>
              {hasConversion && willPayDisplay && (
                <div style={{ ...metricCardStyle, marginTop: "10px", background: "#f4f8ff", minHeight: "auto", flexDirection: "row", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "6px 10px" }}>
                  <p style={{ margin: 0, color: "#3f6fb2", fontSize: "14px", lineHeight: "18px", fontWeight: 600 }}>{Drupal.t("You will pay")}</p>
                  <p style={{ margin: 0, color: "#29528e", fontSize: isMobile ? "18px" : "20px", lineHeight: isMobile ? "22px" : "24px", fontWeight: 700 }}>{willPayDisplay}</p>
                </div>
              )}
              {availableBalance && (
                <div style={{ ...metricCardStyle, marginTop: "10px", background: "#f4f8ff", minHeight: "auto", flexDirection: "row", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "6px 10px" }}>
                  <p style={{ margin: 0, color: "#3f6fb2", fontSize: "14px", lineHeight: "18px", fontWeight: 600 }}>{Drupal.t("Available balance post-transfer")}</p>
                  <p style={{ margin: 0, color: "#29528e", fontSize: isMobile ? "18px" : "20px", lineHeight: isMobile ? "22px" : "24px", fontWeight: 700 }}>{availableBalance}</p>
                </div>
              )}
            </div>
          )}

          {receiptHref && (
            <div style={{ marginBottom: isMobile ? "12px" : "14px" }}>
              <p style={detailRowStyle}>
                <span style={labelStyle}>{Drupal.t("Invoice")}</span>
                <span style={valueStyle}>
                  <a
                    href={receiptHref}
                    className="use-ajax view-receipt-transaction"
                    style={{
                      background: "url(/themes/custom/naut_zero/assets/common/receipt.svg) no-repeat",
                      backgroundPosition: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid #e0e322",
                      padding: "6px 10px 6px 35px",
                      width: "max-content",
                      transition: "all .5s ease",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    {Drupal.t("View Receipt")}
                  </a>
                </span>
              </p>
            </div>
          )}

          {(bankName || recipientName || recipientAddress) && (
            <div style={{ padding: "14px 0" }}>
              <p style={sectionHeaderStyle}>{Drupal.t("Recipient")}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {renderRow(`${Drupal.t("Bank")}`, bankName)}
                {renderRow(`${Drupal.t("Name")}`, recipientName)}
                {renderRow(`${Drupal.t("Address")}`, recipientAddress)}
              </div>
            </div>
          )}

          {(transferMethod || clearingSystem || routingNumber || accountNumber) && (
            <div style={{ padding: "14px 0" }}>
              <p style={sectionHeaderStyle}>{Drupal.t("Transfer details")}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {renderRow(`${Drupal.t("Method")}`, transferMethod)}
                {renderRow(`${Drupal.t("Clearing")}`, clearingSystem)}
                {renderRow(`${Drupal.t("Routing no.")}`, routingNumber)}
                {renderRow(`${Drupal.t("Account no.")}`, accountNumber)}
              </div>
            </div>
          )}

          {confirmation?.error && (
            <p className="red" style={{ fontSize: "14px", lineHeight: "20px", textAlign: "center", fontWeight: "100" }}><strong>{confirmation.error}</strong></p>
          )}
        </div>

        <div className="authorise-bank-transfer-actions step1-button gap-3 mt-2">
            <button
              type="button"
              className="secondary_btn"
              onClick={() => {
                openAdminBankTransfersModalAfterReactClose();
                setStep("close");
              }}
              disabled={loading}
            >
              {Drupal.t("Go Back")}
            </button>
            <button
              type="button"
              className="primary_btn"
              onClick={() => setShowAuthorizeConfirm(true)}
              disabled={loading || confirmation?.error}
              style={confirmation?.error ? {
                cursor: "not-allowed",
                opacity: "0.5"
              } : null}
            >
              {Drupal.t("Authorize")}
            </button>
          </div>
          </>
        )}
      </div>
    </>
  );
};
